import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import React from 'react';
import { Link, generatePath, useLocation } from 'react-router-dom';
import { ArrowRight2 } from '@/components/icons';
import { PatientAvatar } from '@/components/patients/PatientAvatar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PatientDetailPage } from '@/pages/constants';
import type { ReportData } from '../../preview/types';

type ReportPatient = NonNullable<ReportData['patient']>;

export type ExamDateField = 'tests_performed_at_start' | 'tests_performed_at_end';

/**
 * The card shows the birth date rather than an age on purpose: an age here
 * would be the age *today*, sitting directly above a box labelled "Idade no
 * exame" holding a different number, and the two would read as a contradiction.
 * The date is what the exam age is derived from, so it can only agree with it.
 */
const formatBirthDate = (dateOfBirth?: string) => {
    if (!dateOfBirth) return null;

    const [datePart] = dateOfBirth.split('T');
    const [year, month, day] = datePart.split('-').map(Number);

    if (!year || !month || !day) return null;

    return format(new Date(year, month - 1, day), 'dd/MM/yyyy', { locale: ptBR });
};

interface EvaluationPeriodPanelProps {
    patient?: ReportPatient;
    startDate?: string;
    endDate?: string;
    maxDate: string;
    onExamDateChange: (field: ExamDateField, value: string) => void;
}

/**
 * What the patient step shows once the report exists.
 *
 * The patient is locked at this point, so it is presented as context — a card
 * naming who this laudo is about, with a way back to their record — rather than
 * as a form control that cannot be operated. What is left to fill in is the
 * evaluation period, and the age at exam it produces.
 */
export const EvaluationPeriodPanel: React.FC<EvaluationPeriodPanelProps> = ({
    patient,
    startDate,
    endDate,
    maxDate,
    onExamDateChange,
}) => {
    const location = useLocation();
    // The patient page sends its back button here, and `search` is what carries
    // `?id=<reportId>` — without it "back" lands on a blank new report.
    const backTo = `${location.pathname}${location.search}`;

    const birthDate = formatBirthDate(patient?.date_of_birth);

    const hasCalculatedExamAge =
        !!startDate &&
        !!patient?.date_of_birth &&
        patient?.age != null &&
        patient?.age_months != null &&
        patient?.age_days != null;

    return (
        <div className="space-y-6">
            {patient ? (
                <div className="flex items-center gap-3 rounded-xl border border-border bg-surface p-3">
                    <PatientAvatar
                        name={patient.full_name}
                        avatarUrl={patient.avatar_url}
                        className="size-10"
                        fallbackClassName="text-sm"
                    />
                    <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-foreground">
                            {patient.full_name}
                        </p>
                        {birthDate ? (
                            <p className="text-xs tabular-nums text-muted-foreground">
                                Nascimento: {birthDate}
                            </p>
                        ) : null}
                    </div>
                    {patient.id ? (
                        <Link
                            to={`/${generatePath(PatientDetailPage, { patientId: patient.id })}`}
                            state={{ backTo }}
                            className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-brand no-underline hover:underline"
                        >
                            Ver paciente
                            <ArrowRight2 className="size-3" />
                        </Link>
                    ) : null}
                </div>
            ) : null}

            <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-4">
                <div className="space-y-2">
                    <Label htmlFor="tests_performed_at_start">Data de início da avaliação</Label>
                    <Input
                        id="tests_performed_at_start"
                        type="date"
                        max={maxDate}
                        value={startDate?.split('T')[0]}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            onExamDateChange('tests_performed_at_start', e.target.value)
                        }
                    />
                </div>
                <div className="space-y-2">
                    <Label htmlFor="tests_performed_at_end">Data de término da avaliação</Label>
                    <Input
                        id="tests_performed_at_end"
                        type="date"
                        max={maxDate}
                        min={startDate?.split('T')[0]}
                        value={endDate?.split('T')[0]}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            onExamDateChange('tests_performed_at_end', e.target.value)
                        }
                    />
                </div>
            </div>

            <div className="space-y-2">
                <Label>Idade no exame</Label>
                <div className="flex h-10 items-center gap-2 rounded-md border border-border bg-surface px-3 py-2 text-sm">
                    {/* @TODO[Napoli] - We need i18n here to easily pluralize those values */}
                    {hasCalculatedExamAge ? (
                        <span className="font-medium">
                            {patient?.age} anos, {patient?.age_months || 0} meses,{' '}
                            {patient?.age_days || 0} dias
                        </span>
                    ) : (
                        <span className="text-muted-foreground">Informe a data de início</span>
                    )}
                </div>
            </div>
        </div>
    );
};
